import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Building2, Phone, Mail, MessageCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientDistributorPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [distributor, setDistributor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/client/my-distributor`).then(res => {
      setDistributor(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-emerald-100 border-t-emerald-500 animate-spin" />
      </div>
    );
  }

  if (!distributor) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="no-distributor-page">
        <button onClick={() => navigate('/client/help')} className="flex items-center gap-2 text-sm text-gray-500 mb-5 hover:text-gray-700 transition-colors" data-testid="back-to-help">
          <ArrowLeft size={16} /> Ayuda
        </button>
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center">
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sin distribuidor asignado</p>
          <p className="text-sm text-gray-400 mt-1">Aun no tienes un distribuidor vinculado a tu cuenta</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in" data-testid="client-distributor-page">
      <button onClick={() => navigate('/client/help')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-to-help">
        <ArrowLeft size={16} /> Ayuda
      </button>

      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Mi Distribuidor
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">Perfil y datos de contacto</p>
      </div>

      {/* Profile card */}
      <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-3xl p-6 text-white shadow-lg shadow-emerald-500/20" data-testid="distributor-profile-card">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <span className="text-3xl font-bold">{distributor.name?.charAt(0)?.toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-xl truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{distributor.name}</p>
            {distributor.display_id && (
              <p className="text-xs text-white/60 font-mono mt-0.5">{distributor.display_id}</p>
            )}
            {distributor.distributor_type && (
              <span className="inline-block text-xs bg-white/20 backdrop-blur rounded-full px-3 py-0.5 mt-1.5 font-medium">
                {distributor.distributor_type}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-3">
        {distributor.company && (
          <div className="bg-white rounded-2xl border border-gray-100/50 p-4 shadow-sm flex items-center gap-3" data-testid="dist-company">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <Building2 size={18} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Empresa</p>
              <p className="text-sm font-semibold text-gray-900">{distributor.company}</p>
            </div>
          </div>
        )}

        {distributor.phone && (
          <a href={`tel:${distributor.phone}`} className="bg-white rounded-2xl border border-gray-100/50 p-4 shadow-sm flex items-center gap-3 hover:shadow-md transition-all" data-testid="dist-phone">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Phone size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Telefono</p>
              <p className="text-sm font-semibold text-gray-900">{distributor.phone}</p>
            </div>
          </a>
        )}

        {distributor.email && (
          <a href={`mailto:${distributor.email}`} className="bg-white rounded-2xl border border-gray-100/50 p-4 shadow-sm flex items-center gap-3 hover:shadow-md transition-all" data-testid="dist-email">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <Mail size={18} className="text-amber-600" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Correo</p>
              <p className="text-sm font-semibold text-gray-900">{distributor.email}</p>
            </div>
          </a>
        )}

        <button
          onClick={() => navigate('/client/messages')}
          className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-2xl p-4 flex items-center gap-3 shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
          data-testid="message-distributor-btn"
        >
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <MessageCircle size={18} />
          </div>
          <div className="text-left">
            <p className="font-bold text-sm">Enviar mensaje</p>
            <p className="text-xs text-white/70">Chatea con tu distribuidor</p>
          </div>
        </button>
      </div>
    </div>
  );
};

export default ClientDistributorPage;
